import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { JVInput } from "../../shared/JVInput";
import { JVInputCheck } from "../../shared/JVInputCheck";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { JVDropdown } from "../../shared/JVDropdown";
import { RadioButton } from "primereact/radiobutton";
import { format } from 'date-fns';
import { EmptyECI, TECI, TECIRecord } from "./modal";
import { TDropdown } from "../../shared/modal";
import { Fetch } from "../../utilities/fetch";
import { ISubmittals } from "../requirement-my-sales/SubmissionTable";
import { Calendar } from "primereact/calendar";
import { classNames } from "primereact/utils";
import { useToast } from "../../context/toastProvider";

const schema = yup.object({
    submissionId: yup.number().required("Select Candidate").min(1, "Select Candidate"),
    ecI_Date_Time: yup.date().required("Date and time are required").typeError("Date and time are required"),
    timeZoneId: yup.number().min(1, "Zone is required").required("Zone is required"),
    interviewProcessId: yup.number().min(1, "ECI Type is required").required("ECI Type is required"),
    client: yup.string().nullable(),
    interviewrName: yup.string().nullable(),
    ecI_StatusId: yup.number().min(1, "Status is required").required("Status is required"),
    comment: yup.string().nullable(),
    email_Subject: yup.string(),
});

interface Iprops {
    rData?: TECI;
    data: TECIRecord;
    open: boolean;
    hideDialog: () => void;
    reload: () => void;
}

type STDropdown = TDropdown & { isPositive: boolean; readyToNext: boolean }

export const Form = (props: Iprops) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [reqSubms, setReqSubms] = useState<ISubmittals[] | null>(null);
    const [timeZome, setTimeZone] = useState<TDropdown[]>();
    const [ecIStatus, setEciStatus] = useState<STDropdown[]>([]);
    const [interviewProcess, setInterviewProcess] = useState<Array<TDropdown>>();

    const { control, handleSubmit, formState: { errors }, reset, setValue } = useForm<TECIRecord>({
        resolver: yupResolver(schema),
        shouldFocusError: false,
        defaultValues: props.data
    });

    useEffect(() => {
        loadStatus();
        loadTimeZone();
        loadInterviewProcess();
    }, []);

    useEffect(() => {
        reset(props.data);
    }, [props.data]);

    const loadTimeZone = async () => {
        const data = await Fetch<TDropdown[]>("dropdown/time-zone");
        if (data) {
            setTimeZone(data);
        }
    }

    const loadStatus = async () => {
        const data = await Fetch<STDropdown[]>("pipeline/status/4");
        if (data) {
            setEciStatus(data);
        }
    }

    const loadInterviewProcess = async () => {
        const data = await Fetch<TDropdown[]>("dropdown/interviewProcess");
        if (data) {
            setInterviewProcess(data);
        }
    }

    const saveToDB = async (body: TECIRecord) => {
        setLoading(true);

        const method = props.data.id ? "PUT" : "POST";
        const message = props.data.id ? "Updated Successfully" : "Added Successfully";

        const response = await Fetch(props.data.id ? `ECI/${props.data.id}` : "ECI", {
            method, body,
            onError: (m) => showToast({ severity: 'error', detail: m })
        });
        if (response) {
            onHide();
            props.reload();
            showToast({ severity: 'success', detail: message });
        }
        setLoading(false);
    }

    const onHide = () => {
        props.hideDialog();
        reset(EmptyECI);
        setReqSubms(null);
    }

    const idSchema = yup.object().shape({
        id: yup.number().required().min(1)
    });

    const searchReq = async () => {
        const id = control._formValues.reqId;
        const valid = await idSchema.isValid({ id: id, });

        if (valid) {
            setLoading(true);
            const response = await Fetch<ISubmittals[]>(`eci/search/${id}`, {
                onError: (m) => showToast({ severity: 'error', detail: m })
            });
            if (response) {
                setReqSubms(response);
                if (response.length === 1) setValue("submissionId", response[0].submissionId);
            } else {
                setValue("submissionId", 0); setReqSubms(null);
            }
            setLoading(false);
        }
        else showToast({ severity: 'error', detail: "Please Enter a Valid Req ID" });
    }

    function onSubmit(data: TECIRecord) {
        if (!(ecIStatus.find(f => f.id === Number(data.ecI_StatusId))?.isPositive) && !data.comment?.trim()) {
            showToast({ severity: 'error', detail: "Write comment for a negative status" });
            return;
        }
        saveToDB(data);
    }

    const submissionId = (rowData: ISubmittals) => {
        return (
            <Controller name='submissionId' control={control} render={({ field }) => (
                <RadioButton {...field} checked={rowData.submissionId === control._formValues.submissionId} value={rowData.submissionId} />
            )} />
        );
    };


    return (
        <Dialog visible={props.open} style={{ width: '80vw' }} header="Add/Edit ECI" modal className="p-fluid" onHide={onHide}>
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid">
                {!props.data.submissionId ? <>
                    <JVInputCheck column={3} placeholder="Requirement ID" control={control} name="reqId" func={searchReq} />
                    {(reqSubms && reqSubms.length > 0) && (
                        <div className="field col-12 md:col-12">
                            <DataTable size="small" value={reqSubms} lazy dataKey="submissionId">
                                <Column field="submissionId" header="Submission Id" body={submissionId} />
                                <Column field="candidateName" header="Candidate Name" />
                                <Column field="recruiterName" header="Recruiter Name" />
                                <Column field="buying_Rate" header="Buying Rate" />
                                <Column field="submittal_Rate" header="Submittal Rate" body={(s: ISubmittals) => s.submittal_Rate ? s.submittal_Rate : '--'} />
                                <Column field="submissionStatus" header="Submission Status" />
                                <Column field="submital_Date" header="Submission Date" body={(s: ISubmittals) => format(new Date(s.submital_Date + 'z'), "MM-dd-yyyy hh:mm a")} />
                            </DataTable>
                        </div>
                    )}</> :
                    <DataTable className="m-4" style={{ border: '1px solid rgba(105,105,105,0.17)', borderTopWidth: "0" }}
                        size="small" value={[props.rData]}>
                        <Column field="requirementId" header="Req ID" />
                        <Column field="salesManager" header="Sales Manager" />
                        <Column field="recruiter" header="Recruiter" />
                        <Column field="candidateName" header="Consultant Name" />
                        <Column field="jobTitle" header="Job Title" />
                        <Column field="subVendorCompanyName" header="Third Party" />
                        <Column field="vendorCompanyName" header="Prime Vendor" />
                        <Column field="endClient" header="End Client" />
                    </DataTable>}

                <div style={{ margin: '10px 0', width: '100%' }}></div>
                <div className='field col-12 md:col-4'>
                    <label htmlFor='eci_Date_Time'>Date & Time<span style={{ color: 'red' }}>*</span></label>
                    <Controller name='ecI_Date_Time' control={control} render={({ field, fieldState }) => (
                        <Calendar {...field} className={classNames({ 'p-invalid': fieldState.invalid })}
                            minDate={new Date()} showTime hourFormat="12" showIcon />
                    )} />
                    <small className="p-error" >{errors.ecI_Date_Time?.message}</small>
                </div>

                <JVDropdown column={3} label="Time Zone" required control={control} name="timeZoneId" optionLabel="name"
                    optionValue="id" options={timeZome} placeholder="Select Time Zone" error={errors.timeZoneId?.message} />

                <JVDropdown column={3} label="Type" required control={control} name="interviewProcessId" optionLabel="name" optionValue="id"
                    options={interviewProcess} placeholder="Select ECI Type" error={errors.interviewProcessId?.message} />

                <JVInput column={3} label="Client" control={control} name="client" />
                <JVInput column={3} label="Interviewer Name" control={control} name="interviewrName" error={errors.interviewrName?.message} />

                <JVDropdown column={3} label="Status" required control={control} name="ecI_StatusId" optionLabel="name"
                    optionValue="id" options={props.data.id ? ecIStatus : ecIStatus.filter(x => x.name === "ECI Scheduled")} placeholder="Select Status" error={errors.ecI_StatusId?.message} />
                <JVInput column={3} label="Comment" control={control} name="comment" error={errors.comment?.message} />

                <div className="field col-12 md:col-12">
                    <Button loading={loading} className="field col-12 md:col-6" type="submit" label="SAVE" />
                    <Button className="className=field col-12 md:col-6 p-button-outlined" label="CLOSE" onClick={onHide} type="button" />
                </div>

            </form>
        </Dialog>
    );
}
